'use client'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from '@/hooks/use-toast'
import { useApiFetch } from '@/features/auth'
import {
  createRecommendation,
  fetchInvestorRecommendations,
  fetchMyRecommendations,
  updateRecommendationStatus,
  type CreateRecommendationInput,
} from '@/features/recommendations/api/recommendations.api'
import type { Recommendation, RecommendationStatus } from '@/features/recommendations/types'
import { AppError } from '@/lib/errors/appError'
import { queryKeys } from '@/lib/queryKeys'

function toastError(error: unknown) {
  toast({ variant: 'destructive', title: AppError.fromUnknown(error).message })
}

export function useInvestorRecommendations(investorId: string) {
  const authedFetch = useApiFetch()
  return useQuery({
    queryKey: queryKeys.recommendations.byInvestor(investorId),
    queryFn: () => fetchInvestorRecommendations(authedFetch, investorId),
  })
}

export function useMyRecommendations() {
  const authedFetch = useApiFetch()
  return useQuery({
    queryKey: queryKeys.recommendations.mine,
    queryFn: () => fetchMyRecommendations(authedFetch),
  })
}

export function useCreateRecommendation(investorId: string) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateRecommendationInput) =>
      createRecommendation(authedFetch, investorId, input),
    onSuccess: (created) => {
      queryClient.setQueryData(
        queryKeys.recommendations.byInvestor(investorId),
        (old: Recommendation[] = []) => [created, ...old],
      )
      toast({ variant: 'success', title: 'Recommendation sent' })
    },
    onError: toastError,
  })
}

// Shared by two different callers — staff dismissing from a per-investor list, an investor
// accepting/dismissing from their own list — so the query key to update is passed in rather than
// hardcoded.
export function useUpdateRecommendationStatus(invalidateKey: readonly unknown[]) {
  const authedFetch = useApiFetch()
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: RecommendationStatus }) =>
      updateRecommendationStatus(authedFetch, id, status),
    onSuccess: (updated) => {
      queryClient.setQueryData(invalidateKey, (old: Recommendation[] = []) =>
        old.map((r) => (r.id === updated.id ? updated : r)),
      )
      toast({
        variant: 'success',
        title:
          updated.status === 'accepted' ? 'Recommendation accepted' : 'Recommendation dismissed',
      })
    },
    onError: toastError,
  })
}
